import { motion } from 'framer-motion';
import SectionTitle from '../../components/ui/SectionTitle';
import Button from '../../components/ui/Button';
import { MenuItemWithCategory, Category } from '../../lib/supabase/types';
import { useLanguage } from '../../lib/i18n/LanguageContext';
import {
  useLocalizedName,
  useLocalizedDescription,
  useNumberFormatter,
} from '../../lib/utils/formatters';

interface MenuContentProps {
  categoriesWithSlugs: (Category & { slug: string })[];
  groupedMenuItems: Record<string, MenuItemWithCategory[]>;
}

const MenuContent = ({ categoriesWithSlugs, groupedMenuItems }: MenuContentProps) => {
  const { t } = useLanguage();
  const getLocalizedName = useLocalizedName();
  const getLocalizedDescription = useLocalizedDescription();
  const { formatPrice } = useNumberFormatter();

  return (
    <motion.section
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="pt-24 md:pt-48 pb-16 md:pb-16 pb-32 bg-neutral-50"
    >
      <div className="container-custom px-4 md:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto mb-12">
          <h2 className="text-neutral-700 text-center text-lg font-medium mb-4">
            Authentic Italian Menu - Best Pizza & Pasta in Reykjavik
          </h2>
          <p className="text-neutral-600 text-center leading-relaxed">
            Welcome to the best Italian restaurant in Reykjavik. Our menu features authentic
            Neapolitan pizza, fresh pasta, and traditional Italian dishes made with imported Italian
            ingredients and local Icelandic produce. Located in downtown Reykjavik on Laugavegur,
            we've been serving authentic Italian cuisine since 2006.
          </p>
        </div>
        <div className="space-y-20">
          {categoriesWithSlugs.map(category => (
            <div key={category.id} id={category.slug} className="scroll-mt-[140px]">
              <SectionTitle title={getLocalizedName(category)} align="left" className="mb-10" />

              <div className="space-y-0 md:grid md:grid-cols-2 md:gap-8 overflow-y-hidden">
                {(groupedMenuItems[category.slug] || []).map(item => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4 }}
                    className="md:bg-white md:rounded-lg md:overflow-hidden md:shadow-md md:hover:shadow-lg transition-shadow relative border-b border-neutral-800 md:border-none last:border-b-0"
                  >
                    <div className="px-0 py-4 md:p-4 lg:p-6 w-full">
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-serif text-lg sm:text-xl font-semibold text-neutral-900 mb-2">
                            {getLocalizedName(item)}
                          </h3>
                          <p className="text-neutral-600 text-sm leading-relaxed">
                            {getLocalizedDescription(item)}
                          </p>
                        </div>
                        <div className="flex-shrink-0 text-right">
                          <span className="font-medium text-red-800 text-base">
                            {formatPrice(item.price)} ISK
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <p className="text-neutral-700 mb-4">{t.menu.allergyNote}</p>
          <Button variant="primary" onClick={() => (window.location.href = '/reservations')}>
            {t.menu.bookTable}
          </Button>
        </div>
      </div>
    </motion.section>
  );
};

export default MenuContent;
